<template>
  <!--
    「预订最近一班」这条路的等待。

    和「更换时间」那条**性质相反**：这里已经在创建订单了，不可中断、屏幕上也没有
    别的可用内容，用户只关心「钱扣了吗、成了吗」。所以全屏遮罩是对的 ——
    它顺便还挡住了重复点击（h5#7 那个重复下单）。问题从来不是「该不该遮」，
    是遮住之后什么都不说。
  -->
  <div class="booking-progress">
    <div
      ref="cardEl"
      class="booking-progress__card"
    >
      <!-- 上半＝身份。用户早就选好了，不该抢戏，所以压到最小可辨认。 -->
      <p class="booking-progress__title">{{ summary.title || '-' }}</p>
      <p
        v-if="summary.place"
        class="booking-progress__place"
      >
        {{ summary.place }}
      </p>

      <!--
        下半＝承诺，也是进度条本身（Zack）。

        进度不再是旁边的一个部件 —— 它**就是框住「时间 + 金额」那个框的底**，
        从左往右把这笔订单填满。语义直接对上了：正在锁定的就是框里这两样东西。

        用填充而不是「描边跑一圈」：描边的进度量读不出来，填充能。
        渐变右端最亮，scaleX 缩放时那条亮边会自动跟着走 —— 等于白拿一条
        前沿线，不用多加一个元素、也不用做反向缩放。
      -->
      <div
        ref="commitEl"
        class="booking-progress__commit"
      >
        <!--
          进度 = 这个框的描边，从左上角顺时针画。

          用描边不用填充（Zack）：填充会给框内铺一层蓝底，时间和金额压在上面
          —— 而让这两个数好读正是这个框存在的理由。描边把进度画在边界上，
          内容区一点不脏。

          「描边读不出精确进度」这个顾虑在这里不成立：精确到哪一步由下面那行
          文案负责（正在锁定班次 / 订单已创建 / 正在调起支付），描边只负责
          「在推进」。

          ⚠️ 一开始用 pathLength="1" 想把周长归一化，省掉量长度这步。实测不行：
          computed 的 stroke-dasharray 是「1px」，浏览器把它当成 1 个 CSS 像素的
          虚线段在画，归一化根本没吃到（pathLength 在 <rect> 这类基本图形上的
          支持度本来就比 <path> 差）。改成挂载时 getTotalLength() 量真实周长，
          dasharray / dashoffset 都按它算 —— 确定性最高，不赌浏览器。

          尺寸同样是量一次写进属性，不用 CSS 设 SVG 几何属性 ——
          那是 SVG2 的写法，老 WebView 上不稳。
        -->
        <svg
          class="booking-progress__ring"
          aria-hidden="true"
        >
          <rect
            ref="ringEl"
            class="booking-progress__ring-path"
          />
        </svg>

        <div class="booking-progress__when">
          <span class="booking-progress__when-day">{{ summary.day }}</span>
          <span class="booking-progress__when-time din-font-700">{{ summary.time }}</span>
        </div>

        <div
          v-if="summary.price"
          class="booking-progress__price din-font-700"
        >
          <span class="booking-progress__price-unit">{{ summary.currency }}</span>
          <span class="booking-progress__price-value">{{ price.integer }}</span>
          <span
            v-if="price.decimal"
            class="booking-progress__price-decimal"
            >{{ price.decimal }}</span
          >
        </div>
      </div>

      <p
        ref="labelEl"
        class="booking-progress__step"
      >
        {{ stepText }}
      </p>

      <p class="booking-progress__hint">{{ $t('homePage.bookingHint') }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { i18n } from '@/i18n'
import type { BookingStep, BookingSummary } from '@/store/global'

const $t = i18n.global.t

const props = defineProps<{
  step: BookingStep
  summary: BookingSummary
}>()

// 整数 / 小数分开排，和首页卡片的价格是同一套处理（¥18 + 整数 28 + 小数 18）。
// 两处必须一致 —— 用户刚在卡片上看过这个数字，进度里换个排法会让人怀疑不是同一笔。
const price = computed(() => {
  const raw = props.summary.price
  if (!raw) return { integer: '', decimal: '' }
  const { integer, decimal } = separateIntegerAndDecimal(+raw)
  return { integer, decimal: decimal || '' }
})

// 每一步进度推到哪。
//
// ── ② 档位按实测耗时占比分 ────────────────────────────────────────
//
// 原来是写死的 0.25 / 0.55 / 0.8，拍的。实现与推导搬到了
// utils/bookingPace.ts 的 bookingStepTargets —— 搬出去是为了能单测：
// 这段算的是「条子走到哪」和「墙钟过了多少」对不对得上，值得有断言守着。

// 蠕动最多吃掉档距的多少。
//
// 0.4 → 0.85：配合下面按实测时长驱动的减速曲线，条子要能**走到里程碑附近**
// 才谈得上真实；0.4 的上限意味着无论等多久都只走到档距的四成，那本身就是
// 一种「卡住」。仍然刻意小于 1 —— 只有真实事件能推到下一档。
const CREEP_CEILING = 0.85
// 爬行时长相对预期耗时的倍数。配 power2.out：
// 走到预期时刻（进度 1/1.6 = 0.625）时，曲线已完成 1-(1-0.625)² ≈ 0.86，
// 也就是档距的 0.86 × 0.85 ≈ 73% —— 里程碑正常落地时条子就在它附近，
// 不会再有那一下突跳；真慢下来也只是越走越慢地贴近上限，不会假装完成。
const CREEP_STRETCH = 1.6

const STEP_TEXT: Record<string, string> = {
  locking: 'homePage.bookingLocking',
  created: 'homePage.bookingCreated',
  paying: 'homePage.bookingPaying',
  // done 实际上看不到 —— setAppLoad(false) 和它同时发生，遮罩当场就收了。
  // 留着是为了 bookingStepTargets() 里那个 1 有个对应的文案，不至于空着。
  done: 'homePage.bookingPaying',
}

const cardEl = ref<HTMLElement | null>(null)
const commitEl = ref<HTMLElement | null>(null)
const ringEl = ref<SVGRectElement | null>(null)
const labelEl = ref<HTMLElement | null>(null)

// 圆角要和 .booking-progress__commit 的 border-radius 对上（1.2rem）。
// rem 基准是 viewWidth/39，所以这里按运行时算，别写死 12。
// 2px，不是 1px。1px 的蓝线压在框自带的那道灰边上几乎看不出来（Zack：太弱）。
// 描边是这里唯一的进度信号，它必须比"边框"更像"一条线"。
const RING_STROKE = 2
// 圆角矩形的真实周长，measureRing 里量出来。进度 p 对应的 dashoffset
// 就是 ringLength * (1 - p) —— 0 画满，ringLength 一点没画。
let ringLength = 0
let ringReady = false

const measureRing = () => {
  const box = commitEl.value
  const rect = ringEl.value
  if (!box || !rect) return
  const { width, height } = box.getBoundingClientRect()
  if (!width || !height) return
  const radius = parseFloat(getComputedStyle(box).borderTopLeftRadius) || 0
  const inset = RING_STROKE / 2
  rect.setAttribute('x', String(inset))
  rect.setAttribute('y', String(inset))
  rect.setAttribute('width', String(Math.max(width - RING_STROKE, 0)))
  rect.setAttribute('height', String(Math.max(height - RING_STROKE, 0)))
  rect.setAttribute('rx', String(Math.max(radius - inset, 0)))

  // 属性写完才量得准
  ringLength = rect.getTotalLength?.() || 0
  if (!ringLength) return
  rect.style.strokeDasharray = String(ringLength)

  // ⚠️ 初值只设一次。applyStep 每次换步都会重新量（字号/语言变了框会变高），
  // 每次都把 dashoffset 拉回满的话，进度会先弹回 0 再冲出去。
  if (!ringReady) {
    ringReady = true
    rect.style.strokeDashoffset = String(ringLength)
    // 量到之前一直 hidden，避免首帧闪一整圈蓝边
    rect.style.visibility = 'visible'
  }
}

/** 进度（0~1）换成 dashoffset */
const ringOffset = (progress: number) => ringLength * (1 - progress)

const stepText = computed(() => {
  const key = STEP_TEXT[props.step || 'locking']
  return key ? $t(key) : ''
})

let gsapLib: any = null
// ⚠️ 必须分两组。
//
// 卡片入场和「步骤推进」是两件事：applyStep 每次换步都要 kill 掉上一步的
// 进度/文案 tween，而卡片入场只该在卸载时才 kill。一开始两组共用一个数组，
// 结果 onMounted 里先起入场、紧接着 applyStep 的 killAll 把它掐死在
// autoAlpha: 0 上 —— 遮罩出来了、卡片一个字都看不见（实测踩到）。
let stepTweens: any[] = []
let introTween: any = null

const loadGsap = async () => {
  // 动态引，和 m-appointment-panel / home-flight-entry 一个做法 ——
  // vite 的 manualChunks 把 gsap 挡在 vendor 外，不进首屏字节。
  if (!gsapLib) gsapLib = (await import('gsap')).gsap
  return gsapLib
}

const reduceMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches === true

const killStepTweens = () => {
  stepTweens.forEach((t) => t.kill())
  stepTweens = []
}

const killAll = () => {
  killStepTweens()
  introTween?.kill()
  introTween = null
}

// 步骤之间的「蠕动」：很慢地往前爬一点点，表示还在跑。
//
// 它**永远爬不到下一档** —— 上限是当前档 + CREEP_CEILING 个档距。
// 所以进度不会自己跑到用户以为要成了、然后卡住。只有真实事件能推到下一档。
const creep = (gsap: any, from: number, to: number, expectMs: number) => {
  const el = ringEl.value
  if (!el || reduceMotion()) return
  const ceiling = from + (to - from) * CREEP_CEILING
  stepTweens.push(
    gsap.to(el, {
      strokeDashoffset: ringOffset(ceiling),
      // 按这台设备上实测的中位耗时来走，不再是固定 6 秒。
      // 原来那个 6 秒是「假」的根源：一段实际 800ms 的等待里条子几乎不动，
      // 里程碑一到就突跳一大格。
      duration: (expectMs * CREEP_STRETCH) / 1000,
      // 减速：越接近上限走得越慢，永远贴不上去。
      ease: 'power2.out',
    }),
  )
}

const applyStep = async (step: BookingStep, prev: BookingStep) => {
  const gsap = await loadGsap()
  if (!ringEl.value) return

  killStepTweens()
  // 字号/语言变化会改框的高度，每次换步重新量一次最省事
  measureRing()

  const targets = bookingStepTargets()
  const to = targets[step || ''] ?? 0
  const next = step === 'locking' ? targets.created : step === 'created' ? targets.paying : 1
  // 下一段预期还要多久。paying 之后是用户在支付面板上的时间，不可预测 ——
  // 那一档不爬，停在 0.8 等真实结果，比瞎爬诚实。
  const expectMs = step === 'locking' ? bookingPace('lock') : step === 'created' ? bookingPace('pay') : 0

  if (reduceMotion()) {
    gsap.set(ringEl.value, { strokeDashoffset: ringOffset(to) })
  } else {
    stepTweens.push(
      gsap.to(ringEl.value, {
        strokeDashoffset: ringOffset(to),
        duration: 0.45,
        ease: 'power2.out',
        overwrite: 'auto',
        onComplete: () => expectMs > 0 && creep(gsap, to, next, expectMs),
      }),
    )

    // 文案换行：新的从下面顶上来。
    // 只做 from —— 文本已经被 Vue 换好了，这里只演它「进来」。
    if (labelEl.value && prev !== step) {
      stepTweens.push(
        gsap.from(labelEl.value, {
          y: 10,
          autoAlpha: 0,
          duration: 0.26,
          ease: 'power2.out',
          overwrite: 'auto',
          clearProps: 'transform,opacity,visibility',
        }),
      )
    }
  }
}

watch(
  () => props.step,
  (step, prev) => {
    applyStep(step, prev as BookingStep)
  },
)

onMounted(async () => {
  measureRing()
  const gsap = await loadGsap()
  if (!cardEl.value) return
  if (!reduceMotion()) {
    introTween = gsap.from(cardEl.value, {
      y: 16,
      autoAlpha: 0,
      duration: 0.3,
      ease: 'power2.out',
      clearProps: 'transform,opacity,visibility',
    })
  }
  applyStep(props.step, '')
})

onBeforeUnmount(killAll)
</script>

<style lang="scss" scoped>
.booking-progress {
  position: fixed;
  inset: 0;
  z-index: 9999;
  // 这里要的是「别碰」，不是「稍等」。
  // 卡片自己有实底，所以遮罩不用压到全黑 —— 留一点页面轮廓，
  // 用户知道自己还在原来那一页上，不是被丢进了另一个世界。
  //
  // 压到 0.55 并加背景模糊（Zack）：模糊本身就在说「后面那层现在不可用」，
  // 于是不必再靠加黑来压住它。比纯加黑更好读 —— 页面轮廓还在，但没有一个
  // 细节能把视线从卡片上勾走。
  background-color: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(0.8rem);
  -webkit-backdrop-filter: blur(0.8rem);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 3.2rem;

  // 一张单据。
  //
  // 上下两块（身份 / 状态）居中，中间那个框保持左右分置（Zack）——
  // 上下靠左的话右半边大片留白，看着像没排完；而框里的时间和金额是一对
  // 需要各自锚边的数，居中反而挤成一坨。层次靠**字号**拉开，不靠对齐方式。
  &__card {
    text-align: center;
    width: 100%;
    max-width: 30rem;
    box-sizing: border-box;
    padding: 2.4rem 2rem;
    // 和底部面板同一个圆角规范（--van-floating-panel-border-radius）
    border-radius: 2rem;
    background-color: $primary-black-color;
    box-shadow: 0 1.2rem 3.2rem rgba(0, 0, 0, 0.5);
    position: relative;

    // 1px 渐变描边，取**底栏那一套**（styles/mixin.scss 的 glass-panel）：
    // 90 度从上到下，rgba(162,168,186,.2) → 透明。
    //
    // 先试过航线卡片那条蓝色渐变（#016BFF → #42BDFF），Zack 看了说「突出了
    // 些」—— 想想也对：那条蓝描边是给**内容卡片**用的，它要在实景底图上把
    // 自己框出来；而这个弹窗压在一层模糊遮罩上，本来就没有东西跟它抢，再上
    // 一圈饱和蓝就变成描边自己在喊。这里要的只是「有个边」。
    //
    // 只取描边，不用整个 glass-panel mixin：卡片要的是实底（见上面 __card
    // 的说明），glass-panel 会把 background 和 backdrop-filter 一起带进来。
    //
    // ⚠️ 用 ::before + mask-composite 挖空，不用 border：
    // border 会占掉 box-sizing 的内部空间，把卡片里的定宽元素顶变形。
    &::before {
      content: '';
      position: absolute;
      inset: 0;
      z-index: 1;
      border-radius: 2rem;
      padding: 0.1rem;
      background: linear-gradient(
        180deg,
        rgba(162, 168, 186, 0.2) 0%,
        rgba(162, 168, 186, 0) 100%
      );
      -webkit-mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
      mask-composite: exclude;
      pointer-events: none;
    }
  }

  // ── 身份：压到最小可辨认 ──────────────────────────────
  &__title {
    color: $font-white-color;
    font-size: 1.5rem;
    font-weight: 500;
    line-height: 2.2rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__place {
    color: $font-sub-color;
    font-size: 1.2rem;
    line-height: 1.7rem;
    margin-top: 0.2rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  // ── 承诺 ＝ 进度条本身 ────────────────────────────────
  &__commit {
    // 框内保持左右分置，不跟着卡片居中
    text-align: left;
    position: relative;
    // 填充是绝对定位铺满的，必须裁在圆角里
    overflow: hidden;
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 1.2rem;
    margin-top: 1.8rem;
    // 高一些，让这两个数在框里有呼吸（Zack）。
    // 上下 2.2 是照着「框内只有一行大字」给的 —— 再矮就挤，再高就散。
    padding: 2.2rem 1.6rem;
    border-radius: 1.2rem;
    // 不给底色（Zack：底框色可以不要，只要描边即可）。
    // 留一道极淡的静态边当"轨道"—— 没有它的话，进度画到哪儿框就只存在到哪儿，
    // 前 25% 看起来就是一条断线，"框住这笔订单"那层意思立不住。
    border: 0.1rem solid rgba(255, 255, 255, 0.1);
  }

  &__ring {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    // 描边压在内容下面也没关系 —— 它只画在边界上，不盖字
    pointer-events: none;
    overflow: visible;
  }

  &__ring-path {
    fill: none;
    stroke: $primary-blue-color;
    stroke-width: 2;
    // dasharray / dashoffset 的初值由 measureRing 按真实周长写进内联样式，
    // 这里不写死 —— 写死的话首帧会闪一下整圈蓝边。
    stroke-linecap: round;
    visibility: hidden;
  }

  // 文字压在填充之上
  &__when,
  &__price {
    position: relative;
    z-index: 1;
  }

  &__when {
    display: flex;
    align-items: baseline;
    gap: 0.6rem;
    min-width: 0;
  }

  &__when-day {
    color: $font-sub-color;
    font-size: 1.4rem;
    line-height: 2rem;
    flex: 0 0 auto;
  }

  &__when-time {
    color: $font-white-color;
    font-size: 2.6rem;
    font-weight: 700;
    line-height: 3rem;
  }

  // 和首页卡片的价格同一套字号（¥18 + 整数 28 + 小数 18）
  &__price {
    display: flex;
    align-items: baseline;
    // hotspot-card 里那个 $price-color 是它的**文件内局部变量**，这里用不到。
    // 全局的 $decorate-color 就是同一个值（#42BDFF），两边颜色一致。
    color: $decorate-color;
    flex: 0 0 auto;

    &-unit {
      font-size: 1.8rem;
      font-weight: 700;
      margin-right: 0.2rem;
    }

    &-value {
      font-size: 2.8rem;
      font-weight: 700;
      line-height: 3.4rem;
    }

    &-decimal {
      font-size: 1.8rem;
      font-weight: 700;
    }
  }

  // ── 状态 ─────────────────────────────────────────────
  &__step {
    color: $font-white-color;
    font-size: 1.4rem;
    line-height: 2rem;
    margin-top: 1.6rem;
  }

  &__hint {
    color: $font-sub-color;
    font-size: 1.2rem;
    line-height: 1.7rem;
    margin-top: 0.2rem;
  }
}
</style>
